import React, { useState, useEffect, useRef } from 'react';
import * as XLSX from 'xlsx';
import { API_URL } from '../../config';

export default function AdminTab({ userRole, users, companies, currentUserId, isDarkMode }) {
  // Debug logy pro props
  console.log('🔍 AdminTab DEBUG - Props:', {
    userRole,
    usersCount: users ? users.length : 'undefined',
    companiesCount: companies ? companies.length : 'undefined',
    currentUserId,
    users: users ? users.slice(0, 3) : 'undefined', // Prvních 3 pro ukázku
    companies: companies ? companies.slice(0, 3) : 'undefined' // Prvních 3 pro ukázku
  });

  const [adminStats, setAdminStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [userCoefficients, setUserCoefficients] = useState([]);
  const [strategicCoefficients, setStrategicCoefficients] = useState([]);
  const [showCoefficientModal, setShowCoefficientModal] = useState(false);
  const [showStrategicCoefficientModal, setShowStrategicCoefficientModal] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [editingCoefficient, setEditingCoefficient] = useState(null);
  const [editingStrategicCoefficient, setEditingStrategicCoefficient] = useState(null);
  const [bmslData, setBmslData] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(() => {
    // Říjen 2025 kde jsou zobchodovaná data
    return '2025-10';
  });
  const [expandedUsers, setExpandedUsers] = useState(new Set()); // Pro rozbalování detailů

  const [newCoefficient, setNewCoefficient] = useState({
    userId: '',
    coefficient: '',
    description: ''
  });
  const [newStrategicCoefficient, setNewStrategicCoefficient] = useState({
    userId: '',
    coefficient: '',
    description: ''
  });
  const [activeTab, setActiveTab] = useState('stats');
  
  // Stavy pro inline editaci koeficientů
  const [editingCoefficientUserId, setEditingCoefficientUserId] = useState(null);
  const [editingDescriptionUserId, setEditingDescriptionUserId] = useState(null);
  const [tempCoefficientValue, setTempCoefficientValue] = useState('');
  const [tempDescriptionValue, setTempDescriptionValue] = useState('');
  
  // Stavy pro inline editaci strategických koeficientů
  const [editingStrategicCoefficientUserId, setEditingStrategicCoefficientUserId] = useState(null);
  const [editingStrategicDescriptionUserId, setEditingStrategicDescriptionUserId] = useState(null);
  const [tempStrategicCoefficientValue, setTempStrategicCoefficientValue] = useState('');
  const [tempStrategicDescriptionValue, setTempStrategicDescriptionValue] = useState('');
  
  // Stavy pro zádržné
  const [retentionSettings, setRetentionSettings] = useState([]);
  const [bulkRetentionSettings, setBulkRetentionSettings] = useState({
    retentionPercentage: '',
    maxRetentionAmount: '',
    applyToAll: false
  });

  // Stavy pro záložku Zádržné - správa
  const [retentionOverviewData, setRetentionOverviewData] = useState([]);
  const [retentionAllRecordsByUser, setRetentionAllRecordsByUser] = useState({});
  const [expandedRetentionUsers, setExpandedRetentionUsers] = useState(new Set());
  const [retentionUserHistory, setRetentionUserHistory] = useState({});
  const [loadingRetentionOverview, setLoadingRetentionOverview] = useState(false);
  const [loadingRetentionHistory, setLoadingRetentionHistory] = useState({});

  // GP hlavní provize per user
  const [gpByUser, setGpByUser] = useState({});

  // Průměrné provize k výplatě za posledních 12 měsíců
  const [avgPayoutStats, setAvgPayoutStats] = useState([]);
  const [loadingAvgPayout, setLoadingAvgPayout] = useState(false);
  const [expandedAvgMonth, setExpandedAvgMonth] = useState(null);

  const [coefficientsMonth, setCoefficientsMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });

  // Celkový přehled
  const [overviewMonth, setOverviewMonth] = useState(() => {
    const now = new Date();
    const d = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
  const [overviewData, setOverviewData] = useState([]);
  const [loadingOverview, setLoadingOverview] = useState(false);

  // Podpora - nastavení provizí
  const [supportSettings, setSupportSettings] = useState({ gpCoefficient: 0.1, requestTypeValues: [{ typeName: 'Tvorba smlouvy', value: 0, includeGp: true }, { typeName: 'Technická podpora', value: 0, includeGp: false }] });
  const [supportSettingsSaving, setSupportSettingsSaving] = useState(false);

  // Ref pro tracking probíhajícího ukládání (zamezí duplicitním voláním)
  const savingDescriptionRef = useRef(false);
  const savingStrategicDescriptionRef = useRef(false);

  // Funkce pro rozbalení/zabalení detailu uživatele
  const toggleUserExpansion = (userName) => {
    const newExpanded = new Set(expandedUsers);
    if (newExpanded.has(userName)) {
      newExpanded.delete(userName);
    } else {
      newExpanded.add(userName);
    }
    setExpandedUsers(newExpanded);
  };

  const fetchAdminStats = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/admin/stats?userId=${currentUserId || ''}`);
      if (response.ok) {
        const data = await response.json();
        setAdminStats(data);
      }
    } catch (error) {
      console.error('Chyba při načítání statistik:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserCoefficients = async () => {
    try {
      const response = await fetch(`${API_URL}/user-coefficients?userId=${currentUserId || ''}`);
      if (response.ok) {
        const data = await response.json();
        // Přidej userName pro každý koeficient
        const coefficientsWithNames = (data.coefficients || []).map(coeff => ({
          ...coeff,
          userName: coeff.userId ? coeff.userId.name : 'Neznámý uživatel'
        }));
        setUserCoefficients(coefficientsWithNames);
      }
    } catch (error) {
      console.error('Chyba při načítání koeficientů:', error);
    }
  };

  const fetchStrategicCoefficients = async () => {
    try {
      const response = await fetch(`${API_URL}/strategic-coefficients?userId=${currentUserId || ''}`);
      if (response.ok) {
        const data = await response.json();
        // Přidej userName pro každý strategický koeficient
        const coefficientsWithNames = (data.coefficients || []).map(coeff => ({
          ...coeff,
          userName: coeff.userId ? coeff.userId.name : 'Neznámý uživatel'
        }));
        setStrategicCoefficients(coefficientsWithNames);
      }
    } catch (error) {
      console.error('Chyba při načítání strategických koeficientů:', error);
    }
  };

  const fetchAllUsers = async () => {
    console.log('🔄 Načítám všechny uživatele...');
    try {
      const response = await fetch(`${API_URL}/commissions/users?userId=${currentUserId || ''}`);
      console.log('📡 Users response status:', response.status);
      if (response.ok) {
        const data = await response.json();
        console.log('✅ Loaded users:', data.users?.length || 0);
        setAllUsers(data.users || []);
      } else {
        console.error('❌ Error loading users:', response.status);
      }
    } catch (error) {
      console.error('❌ Chyba při načítání uživatelů:', error);
    }
  };

  const handleAddCoefficient = async (e) => {
    e.preventDefault();
    console.log('🔍 EDIT DEBUG - handleAddCoefficient called (should NOT happen during edit!)');
    console.log('🔍 EDIT DEBUG - newCoefficient data:', newCoefficient);
    
    if (!newCoefficient.userId || !newCoefficient.coefficient) {
      alert('Vyplňte povinná pole: Uživatel a Koeficient');
      return;
    }

    setLoading(true);
    try {
      const coefficientData = {
        ...newCoefficient,
        coefficient: parseFloat(newCoefficient.coefficient)
      };

      const response = await fetch(`${API_URL}/user-coefficients`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(coefficientData)
      });

      if (response.ok) {
        setShowCoefficientModal(false);
        setNewCoefficient({
          userId: '',
          coefficient: '',
          description: ''
        });
        await fetchUserCoefficients();
      } else {
        const errorData = await response.json();
        alert('Chyba při přidávání: ' + errorData.error);
      }
    } catch (error) {
      alert('Chyba při přidávání koeficientu');
      console.error('Chyba při přidávání koeficientu:', error);
    } finally {
      setLoading(false);
    }
  };

  const deleteCoefficient = async (coefficientId) => {
    if (!window.confirm('Opravdu chcete smazat tento koeficient?')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/user-coefficients/${coefficientId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        await fetchUserCoefficients();
      } else {
        alert('Chyba při mazání koeficientu');
      }
    } catch (error) {
      alert('Chyba při mazání koeficientu');
      console.error('Chyba při mazání koeficientu:', error);
    }
  };

  // === INLINE EDITACE KOEFICIENTŮ ===
  
  const handleSaveCoefficientInline = async (userId, existingCoeffId) => {
    // Pokud je prázdný string, použij výchozí hodnotu 1.0
    const inputValue = tempCoefficientValue.trim();
    const coefficientValue = inputValue === '' ? 1.0 : parseFloat(inputValue);
    
    console.log('💾 handleSaveCoefficientInline DEBUG:', {
      userId,
      existingCoeffId,
      inputValue,
      tempCoefficientValue,
      coefficientValue,
      isNaN: isNaN(coefficientValue),
      isValid: coefficientValue > 0
    });
    
    if (isNaN(coefficientValue) || coefficientValue <= 0) {
      alert('Koeficient musí být kladné číslo');
      setEditingCoefficientUserId(null);
      setTempCoefficientValue('');
      return;
    }

    setLoading(true);
    try {
      if (existingCoeffId) {
        // Aktualizace existujícího
        const payload = { coefficient: coefficientValue };
        console.log('📤 PUT request:', `${API_URL}/user-coefficients/${existingCoeffId}`, payload);
        
        const response = await fetch(`${API_URL}/user-coefficients/${existingCoeffId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        
        console.log('📥 PUT response status:', response.status);
        if (!response.ok) {
          const errorData = await response.json();
          console.error('❌ PUT error:', errorData);
          throw new Error('Chyba při aktualizaci');
        }
      } else {
        // Vytvoření nového
        const response = await fetch(`${API_URL}/user-coefficients`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            userId, 
            coefficient: coefficientValue,
            description: ''
          })
        });
        
        if (!response.ok) throw new Error('Chyba při vytváření');
      }
      
      await fetchUserCoefficients();
      setEditingCoefficientUserId(null);
      setTempCoefficientValue('');
    } catch (error) {
      alert('Chyba při ukládání koeficientu');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveDescriptionInline = async (userId, existingCoeffId) => {
    console.log('🚨 MAIN DESCRIPTION SAVE CALLED:', { userId, existingCoeffId, tempDescriptionValue });
    setLoading(true);
    try {
      if (existingCoeffId) {
        // Aktualizace popisu existujícího koeficientu
        const response = await fetch(`${API_URL}/user-coefficients/${existingCoeffId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ description: tempDescriptionValue })
        });
        
        if (!response.ok) throw new Error('Chyba při aktualizaci');
      } else {
        // Vytvoření nového koeficientu s popisem (default koeficient 1.0)
        const response = await fetch(`${API_URL}/user-coefficients`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            userId, 
            coefficient: 1.0,
            description: tempDescriptionValue
          })
        });
        
        if (!response.ok) throw new Error('Chyba při vytváření');
      }
      
      await fetchUserCoefficients();
      setEditingDescriptionUserId(null);
      setTempDescriptionValue('');
    } catch (error) {
      alert('Chyba při ukládání poznámky');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // === INLINE EDITACE STRATEGICKÝCH KOEFICIENTŮ ===
  
  const handleSaveStrategicCoefficientInline = async (userId, existingCoeffId) => {
    // Pokud je prázdný string, použij výchozí hodnotu 1.0
    const inputValue = tempStrategicCoefficientValue.trim();
    const coefficientValue = inputValue === '' ? 1.0 : parseFloat(inputValue);
    
    if (isNaN(coefficientValue) || coefficientValue <= 0) {
      alert('Koeficient musí být kladné číslo');
      setEditingStrategicCoefficientUserId(null);
      setTempStrategicCoefficientValue('');
      return;
    }

    setLoading(true);
    try {
      if (existingCoeffId) {
        // Aktualizace existujícího
        const response = await fetch(`${API_URL}/strategic-coefficients/${existingCoeffId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ coefficient: coefficientValue })
        });
        
        if (!response.ok) throw new Error('Chyba při aktualizaci');
      } else {
        // Vytvoření nového
        const response = await fetch(`${API_URL}/strategic-coefficients`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            userId, 
            coefficient: coefficientValue,
            description: ''
          })
        });
        
        if (!response.ok) throw new Error('Chyba při vytváření');
      }
      
      await fetchStrategicCoefficients();
      setEditingStrategicCoefficientUserId(null);
      setTempStrategicCoefficientValue('');
    } catch (error) {
      alert('Chyba při ukládání strategického koeficientu');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveStrategicDescriptionInline = async (userId, existingCoeffId) => {
    setLoading(true);
    try {
      if (existingCoeffId) {
        // Aktualizace popisu existujícího koeficientu
        const response = await fetch(`${API_URL}/strategic-coefficients/${existingCoeffId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ description: tempStrategicDescriptionValue })
        });
        
        if (!response.ok) throw new Error('Chyba při aktualizaci');
      } else {
        // Vytvoření nového koeficientu s popisem (default koeficient 1.0)
        const response = await fetch(`${API_URL}/strategic-coefficients`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            userId, 
            coefficient: 1.0,
            description: tempStrategicDescriptionValue
          })
        });
        
        if (!response.ok) throw new Error('Chyba při vytváření');
      }
      
      await fetchStrategicCoefficients();
      setEditingStrategicDescriptionUserId(null);
      setTempStrategicDescriptionValue('');
    } catch (error) {
      alert('Chyba při ukládání poznámky');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const editCoefficient = (coeff) => {
    console.log('🔍 EDIT DEBUG - editCoefficient called with:', coeff);
    const coeffId = coeff._id;
    console.log('🔍 EDIT DEBUG - Setting editingCoefficient to:', coeffId);
    setEditingCoefficient(coeffId);
    setNewCoefficient({
      userId: coeff.userId._id || coeff.userId,
      coefficient: coeff.coefficient.toString(),
      description: coeff.description || ''
    });
    setShowCoefficientModal(true);
  };

  const handleUpdateCoefficient = async (e) => {
    e.preventDefault();
    console.log('🔍 EDIT DEBUG - handleUpdateCoefficient called, editingCoefficient:', editingCoefficient);
    console.log('🔍 EDIT DEBUG - newCoefficient data:', newCoefficient);
    
    if (!newCoefficient.userId || !newCoefficient.coefficient) {
      alert('Vyplňte povinná pole: Uživatel a Koeficient');
      return;
    }

    setLoading(true);
    try {
      // OPRAVA: Při aktualizaci NESMÍME posílat userId v body - jen coefficient a description
      const coefficientData = {
        coefficient: parseFloat(newCoefficient.coefficient),
        description: newCoefficient.description
      };

      const response = await fetch(`${API_URL}/user-coefficients/${editingCoefficient}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(coefficientData)
      });

      if (response.ok) {
        setShowCoefficientModal(false);
        setEditingCoefficient(null);
        setNewCoefficient({
          userId: '',
          coefficient: '',
          description: ''
        });
        await fetchUserCoefficients();
      } else {
        const errorData = await response.json();
        alert('Chyba při aktualizaci: ' + errorData.error);
      }
    } catch (error) {
      alert('Chyba při aktualizaci koeficientu');
      console.error('Chyba při aktualizaci koeficientu:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetCoefficientModal = () => {
    setShowCoefficientModal(false);
    setEditingCoefficient(null);
    setNewCoefficient({
      userId: '',
      coefficient: '',
      description: ''
    });
  };

  // === STRATEGICKÉ KOEFICIENTY ===
  
  const handleAddStrategicCoefficient = async () => {
    if (!newStrategicCoefficient.userId || !newStrategicCoefficient.coefficient) {
      alert('Vyplňte povinná pole: Uživatel a Koeficient');
      return;
    }

    setLoading(true);
    try {
      const coefficientData = {
        ...newStrategicCoefficient,
        coefficient: parseFloat(newStrategicCoefficient.coefficient)
      };

      const response = await fetch(`${API_URL}/strategic-coefficients`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(coefficientData)
      });

      if (response.ok) {
        setShowStrategicCoefficientModal(false);
        setNewStrategicCoefficient({
          userId: '',
          coefficient: '',
          description: ''
        });
        await fetchStrategicCoefficients();
      } else {
        const errorData = await response.json();
        alert('Chyba při přidávání: ' + errorData.error);
      }
    } catch (error) {
      alert('Chyba při přidávání strategického koeficientu');
      console.error('Chyba při přidávání strategického koeficientu:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStrategicCoefficient = async () => {
    if (!newStrategicCoefficient.userId || !newStrategicCoefficient.coefficient) {
      alert('Vyplňte povinná pole: Uživatel a Koeficient');
      return;
    }

    setLoading(true);
    try {
      // OPRAVA: Při aktualizaci NESMÍME posílat userId v body - jen coefficient a description
      const coefficientData = {
        coefficient: parseFloat(newStrategicCoefficient.coefficient),
        description: newStrategicCoefficient.description
      };

      const response = await fetch(`${API_URL}/strategic-coefficients/${editingStrategicCoefficient}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(coefficientData)
      });

      if (response.ok) {
        setShowStrategicCoefficientModal(false);
        setEditingStrategicCoefficient(null);
        setNewStrategicCoefficient({
          userId: '',
          coefficient: '',
          description: ''
        });
        await fetchStrategicCoefficients();
      } else {
        const errorData = await response.json();
        alert('Chyba při aktualizaci: ' + errorData.error);
      }
    } catch (error) {
      alert('Chyba při aktualizaci strategického koeficientu');
      console.error('Chyba při aktualizaci strategického koeficientu:', error);
    } finally {
      setLoading(false);
    }
  };

  const deleteStrategicCoefficient = async (coefficientId) => {
    if (!window.confirm('Opravdu chcete smazat tento strategický koeficient?')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/strategic-coefficients/${coefficientId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        await fetchStrategicCoefficients();
      } else {
        alert('Chyba při mazání strategického koeficientu');
      }
    } catch (error) {
      alert('Chyba při mazání strategického koeficientu');
      console.error('Chyba při mazání strategického koeficientu:', error);
    }
  };

  const editStrategicCoefficient = (coeff) => {
    const coeffId = coeff._id;
    setEditingStrategicCoefficient(coeffId);
    setNewStrategicCoefficient({
      userId: coeff.userId._id || coeff.userId,
      coefficient: coeff.coefficient.toString(),
      description: coeff.description || ''
    });
    setShowStrategicCoefficientModal(true);
  };

  const resetStrategicCoefficientModal = () => {
    setShowStrategicCoefficientModal(false);
    setEditingStrategicCoefficient(null);
    setNewStrategicCoefficient({
      userId: '',
      coefficient: '',
      description: ''
    });
  };

  // Funkce pro načítání nastavení zádržného
  const fetchRetentionSettings = async () => {
    try {
      const response = await fetch(`${API_URL}/retention/settings?userId=${currentUserId || ''}`);
      if (response.ok) {
        const data = await response.json();
        setRetentionSettings(data.settings || []);
      }
    } catch (error) {
      console.error('Chyba při načítání nastavení zádržného:', error);
    }
  };

  // Načítání přehledu zádržného (aktuální výše pro každého obchodníka)
  const fetchRetentionOverview = async () => {
    setLoadingRetentionOverview(true);
    try {
      const response = await fetch(`${API_URL}/retention/monthly?userId=${currentUserId || ''}`);
      if (response.ok) {
        const data = await response.json();
        const records = data.records || [];
        // Seskup všechny záznamy podle userId
        const allByUser = {};
        records.forEach(record => {
          const uid = record.userId?._id || record.userId;
          if (!allByUser[uid]) allByUser[uid] = [];
          allByUser[uid].push(record);
        });
        // Seřaď každému uživateli záznamy sestupně
        Object.values(allByUser).forEach(arr => arr.sort((a, b) => b.month.localeCompare(a.month)));
        setRetentionAllRecordsByUser(allByUser);
        // Předvyřeš historii pro všechny uživatele
        const histMap = {};
        Object.entries(allByUser).forEach(([uid, arr]) => { histMap[uid] = arr; });
        setRetentionUserHistory(histMap);
        // Pro přehled: vezmi nejnovější záznam každého uživatele
        const overview = Object.values(allByUser)
          .map(arr => arr[0])
          .sort((a, b) => (a.userId?.name || '').localeCompare(b.userId?.name || '', 'cs'));
        setRetentionOverviewData(overview);
      }
    } catch (error) {
      console.error('Chyba při načítání přehledu zádržného:', error);
    } finally {
      setLoadingRetentionOverview(false);
    }
  };

  // Helper: formátuj položky uvolnění pro tooltip
  const formatReleasedItemsTooltip = (items) => {
    if (!items || items.length === 0) return '';
    return items.map(item => `${item.companyName || '?'} (${item.commissionType === 'strategic' ? 'strat.' : 'hl.'}): ${(item.amount || 0).toLocaleString('cs-CZ')} Kč`).join('\n');
  };

  // Rozbalení / zabalení histórie obchodníka
  const toggleRetentionUser = async (userId) => {
    const newExpanded = new Set(expandedRetentionUsers);
    if (newExpanded.has(userId)) {
      newExpanded.delete(userId);
      setExpandedRetentionUsers(newExpanded);
      return;
    }
    newExpanded.add(userId);
    setExpandedRetentionUsers(newExpanded);
    // Použij cacheá data pokud jsou dostupná
    if (!retentionUserHistory[userId] && retentionAllRecordsByUser[userId]) {
      setRetentionUserHistory(prev => ({ ...prev, [userId]: retentionAllRecordsByUser[userId] }));
    } else if (!retentionUserHistory[userId]) {
      setLoadingRetentionHistory(prev => ({ ...prev, [userId]: true }));
      try {
        const response = await fetch(`${API_URL}/retention/monthly?userId=${userId}`);
        if (response.ok) {
          const data = await response.json();
          const sorted = (data.records || []).sort((a, b) => b.month.localeCompare(a.month));
          setRetentionUserHistory(prev => ({ ...prev, [userId]: sorted }));
        }
      } catch (error) {
        console.error('Chyba při načítání historie zádržného:', error);
      } finally {
        setLoadingRetentionHistory(prev => ({ ...prev, [userId]: false }));
      }
    }
  };

  // Aktualizace nastavení zádržného pro uživatele
  const updateRetentionSettings = async (userId, settings) => {
    console.log('🔄 Aktualizuji retention settings:', { userId, settings });
    try {
      const response = await fetch(`${API_URL}/retention/settings/${userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(settings)
      });
      
      console.log('📡 Response status:', response.status);
      
      if (response.ok) {
        const result = await response.json();
        console.log('✅ Response data:', result);
        await fetchRetentionSettings(); // Refresh dat
        alert('Nastavení zádržného bylo uloženo');
      } else {
        const errorText = await response.text();
        console.error('❌ Error response:', errorText);
        alert(`Chyba při ukládání nastavení: ${response.status}`);
      }
    } catch (error) {
      console.error('❌ Chyba při aktualizaci nastavení zádržného:', error);
      alert('Chyba při ukládání nastavení');
    }
  };

  // Hromadná aktualizace nastavení
  const applyBulkRetentionSettings = async () => {
    const percentage = bulkRetentionSettings.retentionPercentage ? Number(bulkRetentionSettings.retentionPercentage.replace(',', '.')) : null;
    const maxAmount = bulkRetentionSettings.maxRetentionAmount ? Number(bulkRetentionSettings.maxRetentionAmount) : null;
    
    if (!percentage && !maxAmount) {
      alert('Vyplňte alespoň jeden parametr');
      return;
    }

    if (percentage && (percentage < 0 || percentage > 100)) {
      alert('Procento musí být mezi 0 a 100');
      return;
    }

    if (maxAmount && maxAmount < 0) {
      alert('Maximální fond musí být kladné číslo');
      return;
    }

    console.log('👥 All users:', allUsers.length, allUsers.slice(0, 2)); // Debug info
    
    if (!allUsers || allUsers.length === 0) {
      alert('Nejsou načteni žádní uživatelé. Zkuste obnovit stránku.');
      return;
    }
    
    const userIds = allUsers.map(user => user._id);
    
    console.log('🔄 Bulk update retention settings:', { percentage, maxAmount, userIds: userIds.length });
    
    try {
      const response = await fetch(`${API_URL}/retention/settings/bulk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          retentionPercentage: percentage,
          maxRetentionAmount: maxAmount,
          userIds
        })
      });
      
      console.log('📡 Bulk response status:', response.status);
      
      if (response.ok) {
        const result = await response.json();
        console.log('✅ Bulk response data:', result);
        await fetchRetentionSettings();
        setBulkRetentionSettings({ retentionPercentage: '', maxRetentionAmount: '', applyToAll: false });
        alert('Nastavení bylo aplikováno na všechny uživatele');
      } else {
        const errorText = await response.text();
        console.error('❌ Bulk error response:', errorText);
        alert(`Chyba při hromadné aktualizaci: ${response.status}`);
      }
    } catch (error) {
      console.error('❌ Chyba při hromadné aktualizaci:', error);
      alert('Chyba při hromadné aktualizaci');
    }
  };

  useEffect(() => {
    if (activeTab === 'bmsl') {
      // Načti BMSL data z backendu
      fetchBmslData(selectedMonth);
    } else if (activeTab === 'retention') {
      // Načti nastavení zádržného když se přepne na záložku
      fetchRetentionSettings();
    } else if (activeTab === 'retention-overview') {
      // Načti přehled zádržného
      fetchRetentionOverview();
    } else if (activeTab === 'overview') {
      fetchOverviewData(overviewMonth);
    }
  }, [activeTab, selectedMonth]);

  useEffect(() => {
    if (activeTab === 'overview') {
      fetchOverviewData(overviewMonth);
    }
  }, [overviewMonth]);

  const fetchOverviewData = async (month) => {
    const usersToFetch = allUsers.length > 0 ? allUsers : (users || []);
    if (!usersToFetch || usersToFetch.length === 0) return;
    setLoadingOverview(true);
    try {
      const results = await Promise.all(
        usersToFetch.map(async (user) => {
          const [mainRes, strategicRes, cashbackRes, cashbackStrategicRes, retentionRes] = await Promise.all([
            fetch(`${API_URL}/commissions?month=${month}&userId=${currentUserId}&filterUserId=${user._id}`),
            fetch(`${API_URL}/strategic-commissions?month=${month}&userId=${currentUserId}&filterUserId=${user._id}`),
            fetch(`${API_URL}/cashbacks?month=${month}&userId=${currentUserId}&filterUserId=${user._id}`),
            fetch(`${API_URL}/cashback-strategic-commissions?month=${month}&userId=${currentUserId}&filterUserId=${user._id}`),
            fetch(`${API_URL}/retention/monthly?userId=${user._id}&month=${month}`)
          ]);

          let main = 0, strategic = 0, cashback = 0, cashbackStrategic = 0, retention = 0;

          if (mainRes.ok) {
            const d = await mainRes.json();
            main = d.commissions?.reduce((s, c) => s + (c.commission || 0), 0) || 0;
          }
          if (strategicRes.ok) {
            const d = await strategicRes.json();
            strategic = d.commissions?.reduce((s, c) => s + (c.commission || 0), 0) || 0;
          }
          if (cashbackRes.ok) {
            const d = await cashbackRes.json();
            cashback = d.cashbacks?.reduce((s, c) => s + (c.cashback || 0), 0) || 0;
          }
          if (cashbackStrategicRes.ok) {
            const d = await cashbackStrategicRes.json();
            cashbackStrategic = d.commissions?.reduce((s, c) => s + (c.commission || 0), 0) || 0;
          }
          if (retentionRes.ok) {
            const d = await retentionRes.json();
            retention = d.records?.[0]?.retentionAmount || 0;
          }

          const total = main + strategic + cashback + cashbackStrategic;
          return {
            userId: user._id,
            name: user.name,
            main,
            strategic,
            cashback,
            cashbackStrategic,
            retention,
            toPay: total - retention
          };
        })
      );
      setOverviewData(results);
    } catch (error) {
      console.error('Chyba při načítání celkového přehledu:', error);
    } finally {
      setLoadingOverview(false);
    }
  };

  // BMSL data se nyní načítají přímo z backendu (serverová agregace)
  const fetchBmslData = async (month) => {
    setLoading(true);
    try {
      console.log('🔄 Načítám BMSL data z backendu pro měsíc:', month);
      
      // NOVÉ: Volej backend endpoint místo client-side zpracování
      const response = await fetch(`${API_URL}/admin/bmsl-overview?month=${month}&userId=${currentUserId || ''}`);
      
      if (response.ok) {
        const data = await response.json();
        console.log('✅ BMSL data načtena:', data.length, 'uživatelů');
        setBmslData(data);
      } else {
        console.error('❌ Chyba při načítání BMSL dat:', response.status);
        setBmslData([]);
      }
    } catch (error) {
      console.error('❌ Chyba při načítání BMSL dat:', error);
      setBmslData([]);
    } finally {
      setLoading(false);
    }
  };

  const exportBmslToExcel = () => {
    if (!bmslData || bmslData.length === 0) return;

    const rows = [];
    bmslData.forEach(user => {
      if (user.companies && user.companies.length > 0) {
        user.companies.forEach(company => {
          rows.push({
            'Obchodník': user.userName || 'Neznámý',
            'IČ': company.ico || '',
            'Název firmy': company.name || '',
            'Typ': company.type === 'upsell' ? 'Upsell' : 'Hlavní',
            'BMSL (Kč)': company.bmsl || 0,
            'Datum zobchodování': company.closedDate
              ? new Date(company.closedDate).toLocaleDateString('cs-CZ')
              : ''
          });
        });
      }
    });

    if (rows.length === 0) return;

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'BMSL přehled');
    XLSX.writeFile(wb, `BMSL_prehled_${selectedMonth}.xlsx`);
  };

  const fetchGpByUser = async (month) => {
    try {
      const response = await fetch(`${API_URL}/commissions?month=${month}&userId=${currentUserId || ''}`);
      if (response.ok) {
        const data = await response.json();
        const commissions = data.commissions || [];
        const grouped = {};
        commissions.forEach(c => {
          const uid = c.userId?._id || c.userId;
          if (!uid) return;
          const id = uid.toString();
          grouped[id] = (grouped[id] || 0) + (c.gp || 0);
        });
        setGpByUser(grouped);
      }
    } catch (error) {
      console.error('Chyba při načítání GP dat:', error);
    }
  };

  const fetchAvgPayoutStats = async () => {
    setLoadingAvgPayout(true);
    try {
      const response = await fetch(`${API_URL}/admin/stats/avg-payout?userId=${currentUserId || ''}`);
      if (response.ok) {
        const data = await response.json();
        setAvgPayoutStats(data.data || []);
      }
    } catch (error) {
      console.error('Chyba při načítání průměrných provizí k výplatě:', error);
    } finally {
      setLoadingAvgPayout(false);
    }
  };

  const fetchSupportSettings = async () => {
    try {
      const res = await fetch(`${API_URL}/support-commissions/settings`);
      if (res.ok) {
        const data = await res.json();
        setSupportSettings({
          gpCoefficient: data.gpCoefficient ?? 0.1,
          requestTypeValues: (data.requestTypeValues && data.requestTypeValues.length > 0)
            ? data.requestTypeValues.map(r => ({ ...r, includeGp: r.includeGp !== false }))
            : [{ typeName: 'Tvorba smlouvy', value: 0, includeGp: true }, { typeName: 'Technická podpora', value: 0, includeGp: false }]
        });
      }
    } catch (err) {
      console.error('Chyba při načítání nastavení podpory:', err);
    }
  };

  const saveSupportSettings = async () => {
    setSupportSettingsSaving(true);
    try {
      const res = await fetch(`${API_URL}/support-commissions/settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...supportSettings, updatedBy: currentUserId })
      });
      if (res.ok) {
        alert('Nastavení podpory bylo uloženo');
      } else {
        alert('Chyba při ukládání');
      }
    } catch (err) {
      alert('Chyba: ' + err.message);
    } finally {
      setSupportSettingsSaving(false);
    }
  };

  const recalculateCommissions = async (month) => {
    if (!window.confirm(`Opravdu chcete přepočítat všechny provize pro měsíc ${month}? Tato akce přepíše všechny existující výpočty!`)) {
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/admin/recalculate-commissions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ month, userId: currentUserId })
      });

      if (response.ok) {
        const data = await response.json();
        alert(`Úspěšně přepočítáno ${data.updated} provizí`);
        await fetchAdminStats();
      } else {
        const errorData = await response.json();
        alert('Chyba při přepočtu: ' + errorData.error);
      }
    } catch (error) {
      alert('Chyba při přepočtu provizí');
      console.error('Chyba při přepočtu provizí:', error);
    } finally {
      setLoading(false);
    }
  };

  const recalculateRetention = async (month) => {
    const monthName = new Date(month + '-01').toLocaleDateString('cs-CZ', { year: 'numeric', month: 'long' });
    if (!window.confirm(`Opravdu chcete přepočítat zádržné pro měsíc ${monthName}? Tato akce přepíše existující výpočty zádržného.`)) {
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/admin/recalculate-retention`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ month, userId: currentUserId })
      });

      if (response.ok) {
        const data = await response.json();
        alert(`✅ ${data.message}\n\nPřepočítáno pro ${data.updated} uživatelů.`);
        await fetchAdminStats();
      } else {
        const errorData = await response.json();
        alert('Chyba při přepočtu zádržného: ' + errorData.error);
      }
    } catch (error) {
      alert('Chyba při přepočtu zádržného');
      console.error('Chyba při přepočtu zádržného:', error);
    } finally {
      setLoading(false);
    }
  };

  const deleteRetention = async (month) => {
    const monthName = new Date(month + '-01').toLocaleDateString('cs-CZ', { year: 'numeric', month: 'long' });
    if (!window.confirm(`⚠️ POZOR! Opravdu chcete SMAZAT všechny záznamy zádržného pro měsíc ${monthName}?\n\nTato akce je NEVRATNÁ!`)) {
      return;
    }

    // Druhá kontrola
    if (!window.confirm(`Ještě jednou potvrzení: Smazat zádržné za ${monthName}?`)) {
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/admin/retention/${month}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        const data = await response.json();
        alert(`✅ ${data.message}\n\nSmazáno ${data.deleted} záznamů.`);
        await fetchAdminStats();
      } else {
        const errorData = await response.json();
        alert('Chyba při mazání zádržného: ' + errorData.error);
      }
    } catch (error) {
      alert('Chyba při mazání zádržného');
      console.error('Chyba při mazání zádržného:', error);
    } finally {
      setLoading(false);
    }
  };

  const deleteReleaseItem = async (userId, month, itemId, itemDesc) => {
    if (!window.confirm(`Vrátit zpět do zádržného: ${itemDesc}?\n\nTato částka bude přidána zpět do fondu.`)) return;
    try {
      const res = await fetch(`${API_URL}/retention/release-item/${userId}/${month}/${itemId}`, { method: 'DELETE' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Chyba');
      alert(`\u2705 ${data.message}`);
      // Vymaz cache a znovu načti přehled
      setRetentionUserHistory(prev => { const n = {...prev}; delete n[userId]; return n; });
      setRetentionAllRecordsByUser(prev => { const n = {...prev}; delete n[userId]; return n; });
      await fetchRetentionOverview();
    } catch (err) {
      alert('Chyba při vracení položky: ' + err.message);
    }
  };

  const exportData = async (type, month = '') => {
    try {
      const url = month ? 
        `${API_URL}/admin/export/${type}?month=${month}` : 
        `${API_URL}/admin/export/${type}`;
      
      const response = await fetch(url);
      if (response.ok) {
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = `${type}_${month || 'all'}_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } else {
        alert('Chyba při exportu dat');
      }
    } catch (error) {
      alert('Chyba při exportu dat');
      console.error('Chyba při exportu dat:', error);
    }
  };

  useEffect(() => {
    fetchAdminStats();
    fetchUserCoefficients();
    fetchStrategicCoefficients();
    fetchAllUsers();
    fetchBmslData(selectedMonth);
    fetchSupportSettings();
    fetchAvgPayoutStats();
  }, []);

  useEffect(() => {
    if (activeTab === 'bmsl') {
      fetchBmslData(selectedMonth);
    }
    if (activeTab === 'coefficients') {
      fetchGpByUser(coefficientsMonth);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMonth, activeTab]);

  useEffect(() => {
    if (activeTab === 'coefficients') {
      fetchGpByUser(coefficientsMonth);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coefficientsMonth]);

  // Kontrola oprávnění
  if (userRole !== 'admin' && userRole !== 'superadmin') {
    return (
      <div className="text-center py-8">
        <div className="text-red-600 text-lg font-semibold mb-2">🚫 Nedostatečná oprávnění</div>
        <div className="text-gray-600">Pro přístup k ADMIN sekci potřebujete oprávnění administrátora.</div>
      </div>
    );
  }

  return (
    <div>
      {/* Tab navigace */}
      <div className={`mb-6 ${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-sm border ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
        <div className={`flex ${isDarkMode ? 'border-gray-700' : 'border-gray-200'} border-b`}>
          {[
            { id: 'stats', label: 'Statistiky', icon: '📊' },
            { id: 'overview', label: 'Celkový přehled', icon: '👥' },
            { id: 'bmsl', label: 'BMSL přehled', icon: '📈' },
            { id: 'coefficients', label: 'Hlavní provize', icon: '⚙️' },
            { id: 'strategic-coefficients', label: 'Strategické produkty', icon: '🎯' },
            { id: 'retention', label: 'Zádržné', icon: '🏦' },
            { id: 'retention-overview', label: 'Zádržné - správa', icon: '💼' },
            { id: 'support-commissions', label: 'Podpora', icon: '🛠️' },
            { id: 'tools', label: 'Nástroje', icon: '🛠️' },
            { id: 'export', label: 'Export', icon: '💾' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? `border-blue-500 text-blue-600 ${isDarkMode ? 'bg-blue-900/50' : 'bg-blue-50'}`
                  : `border-transparent ${isDarkMode ? 'text-gray-300 hover:text-gray-100 hover:border-gray-500' : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'}`
              }`}
            >
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Obsah podle aktivní karty */}
      {activeTab === 'stats' && (
        <div>
          <div className="mb-4 flex items-center justify-between">
            <h2 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Systémové statistiky</h2>
            <button
              onClick={fetchAdminStats}
              disabled={loading}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Načítání...' : 'Obnovit'}
            </button>
          </div>

          {/* Layout: Vlevo 2 karty + graf pod nimi, Vpravo zádržné */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
            {/* Levá sekce - 2 sloupce (Celkové provize, Strategické provize) */}
            <div className="lg:col-span-2 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} p-4 rounded-lg shadow border-l-4 border-blue-500`}>
                  <h3 className={`text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} mb-1`}>Celkové provize</h3>
                  <p className="text-2xl font-bold text-blue-600">{adminStats.totalCommissions || 0}</p>
                </div>
                <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} p-4 rounded-lg shadow border-l-4 border-purple-500`}>
                  <h3 className={`text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} mb-1`}>Strategické provize</h3>
                  <p className="text-2xl font-bold text-purple-600">{adminStats.totalStrategicCommissions || 0}</p>
                </div>
              </div>

              {/* Graf vývoje celkových provizí za posledních 12 měsíců */}
              {adminStats.monthlyStats && adminStats.monthlyStats.length > 0 && (
                <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} p-6 rounded-lg shadow`}>
                  <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Vývoj celkových provizí za posledních 12 měsíců</h3>
                  <div className="space-y-2">
                    {adminStats.monthlyStats.slice(-12).map((month) => {
                      const total = (month.regular || 0) + (month.strategic || 0) + (month.cashbacks || 0);
                      const maxTotal = Math.max(...adminStats.monthlyStats.slice(-12).map(m => (m.regular || 0) + (m.strategic || 0) + (m.cashbacks || 0)));
                      const percentage = maxTotal > 0 ? (total / maxTotal) * 100 : 0;
                      
                      return (
                        <div key={month.month} className="flex items-center gap-3">
                          <div className={`text-xs font-medium w-24 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                            {new Date(month.month + '-01').toLocaleDateString('cs-CZ', { year: 'numeric', month: 'short' })}
                          </div>
                          <div className="flex-1 h-8 bg-gray-200 dark:bg-gray-700 rounded-lg overflow-hidden relative">
                            <div 
                              className="h-full bg-gradient-to-r from-blue-500 to-purple-500 transition-all duration-300 flex items-center justify-end pr-2"
                              style={{ width: `${percentage}%` }}
                            >
                              {percentage > 20 && (
                                <span className="text-xs font-semibold text-white">{total.toLocaleString('cs-CZ')}</span>
                              )}
                            </div>
                            {percentage <= 20 && (
                              <span className={`absolute right-2 top-1/2 transform -translate-y-1/2 text-xs font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                {total.toLocaleString('cs-CZ')}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Pravá sekce - Přehled zádržného (max 12 měsíců, scrollovatelné) */}
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} p-6 rounded-lg shadow`}>
              <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Přehled zádržného</h3>
              <div className="space-y-3 max-h-[500px] overflow-y-auto pr-2">
                {/* Placeholder - bude naplněno skutečnými daty zádržného */}
                {adminStats.monthlyStats && adminStats.monthlyStats.length > 0 ? (
                  adminStats.monthlyStats.slice(-12).map((month) => (
                    <div key={month.month} className={`p-3 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
                      <div className={`text-sm font-medium mb-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {new Date(month.month + '-01').toLocaleDateString('cs-CZ', { year: 'numeric', month: 'long' })}
                      </div>
                      <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        Zádržné: <span className="font-semibold text-yellow-600">0 Kč</span>
                      </div>
                      <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        Vyplaceno: <span className="font-semibold text-green-600">0 Kč</span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className={`text-sm text-center py-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Žádná data k zobrazení
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Graf průměrných provizí k výplatě za posledních 12 měsíců */}
          <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} p-6 rounded-lg shadow mb-6`}>
            <h3 className={`text-lg font-semibold mb-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Průměrná provize k výplatě za posledních 12 měsíců</h3>
            <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Zahrnuje jen uživatele s provizí alespoň 10 000 Kč &nbsp;·&nbsp;
              <span className="inline-flex items-center gap-1">
                <span className="inline-block w-3 h-3 rounded-sm bg-green-500"></span> k výplatě
                <span className="inline-block w-3 h-3 rounded-sm bg-yellow-400 ml-1"></span> zádržné
              </span>
              &nbsp;·&nbsp; Kliknutím na řádek zobrazíte detail
            </p>
            {loadingAvgPayout ? (
              <div className={`text-center py-6 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Načítání...</div>
            ) : avgPayoutStats.length === 0 ? (
              <div className={`text-center py-6 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Žádná data k zobrazení</div>
            ) : (
              <div className="space-y-1">
                {(() => {
                  const maxAvg = Math.max(...avgPayoutStats.map(m => m.avgPayout || 0), 1);
                  return avgPayoutStats.map((m) => {
                    const pctTotal = maxAvg > 0 ? ((m.avgPayout || 0) / maxAvg) * 100 : 0;
                    const pctNet = m.avgPayout > 0 ? ((m.avgNet || 0) / m.avgPayout) * pctTotal : 0;
                    const pctRet = pctTotal - pctNet;
                    const isExpanded = expandedAvgMonth === m.month;
                    return (
                      <div key={m.month}>
                        <div
                          className={`flex items-center gap-3 py-1 px-2 rounded-lg cursor-pointer transition-colors ${
                            isExpanded
                              ? (isDarkMode ? 'bg-blue-900/40' : 'bg-blue-50')
                              : (isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50')
                          }`}
                          onClick={() => setExpandedAvgMonth(isExpanded ? null : m.month)}
                        >
                          <div className={`text-xs font-medium w-24 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                            {new Date(m.month + '-01').toLocaleDateString('cs-CZ', { year: 'numeric', month: 'short' })}
                          </div>
                          <div className="flex-1 h-8 bg-gray-200 dark:bg-gray-700 rounded-lg overflow-hidden relative">
                            {/* Zelená část – k výplatě */}
                            <div
                              className="absolute left-0 top-0 h-full bg-gradient-to-r from-green-500 to-green-400 transition-all duration-300"
                              style={{ width: `${pctNet}%` }}
                            />
                            {/* Žlutá část – zádržné */}
                            {pctRet > 0 && (
                              <div
                                className="absolute top-0 h-full bg-yellow-400 transition-all duration-300"
                                style={{ left: `${pctNet}%`, width: `${pctRet}%` }}
                              />
                            )}
                            {/* Popisek */}
                            {pctTotal > 20 ? (
                              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs font-semibold text-white drop-shadow">
                                {(m.avgPayout || 0).toLocaleString('cs-CZ')} Kč
                              </span>
                            ) : (
                              <span className={`absolute right-2 top-1/2 -translate-y-1/2 text-xs font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                {(m.avgPayout || 0).toLocaleString('cs-CZ')} Kč
                              </span>
                            )}
                          </div>
                          <div className={`text-xs w-16 text-right ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            {m.userCount} uživ. {isExpanded ? '▲' : '▼'}
                          </div>
                        </div>
                        {/* Detail uživatelů */}
                        {isExpanded && m.users && m.users.length > 0 && (
                          <div className={`mx-2 mb-2 rounded-lg border overflow-hidden ${
                            isDarkMode ? 'border-gray-600 bg-gray-900/50' : 'border-gray-200 bg-gray-50'
                          }`}>
                            <table className="w-full text-xs">
                              <thead>
                                <tr className={isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-500'}>
                                  <th className="px-3 py-1.5 text-left font-medium">Uživatel</th>
                                  <th className="px-3 py-1.5 text-right font-medium">Hrubá provize</th>
                                  <th className="px-3 py-1.5 text-right font-medium">Zádržné</th>
                                  <th className="px-3 py-1.5 text-right font-medium">K výplatě</th>
                                </tr>
                              </thead>
                              <tbody className={`divide-y ${isDarkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                                {m.users.map((u, i) => (
                                  <tr key={i} className={isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-white'}>
                                    <td className={`px-3 py-1.5 font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{u.name}</td>
                                    <td className={`px-3 py-1.5 text-right ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{(u.total || 0).toLocaleString('cs-CZ')} Kč</td>
                                    <td className="px-3 py-1.5 text-right text-yellow-600 font-medium">{(u.retention || 0).toLocaleString('cs-CZ')} Kč</td>
                                    <td className="px-3 py-1.5 text-right text-green-600 font-semibold">{(u.toPay || 0).toLocaleString('cs-CZ')} Kč</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    );
                  });
                })()}
              </div>
            )}
          </div>

          {/* Cashbacky a Aktivní uživatelé */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} p-4 rounded-lg shadow border-l-4 border-red-500`}>
              <h3 className={`text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} mb-1`}>Cashbacky</h3>
              <p className="text-2xl font-bold text-red-600">{adminStats.totalCashbacks || 0}</p>
            </div>
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} p-4 rounded-lg shadow border-l-4 border-green-500`}>
              <h3 className={`text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} mb-1`}>Aktivní uživatelé</h3>
              <p className="text-2xl font-bold text-green-600">{adminStats.activeUsers || 0}</p>
            </div>
          </div>

          {adminStats.monthlyStats && (
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} p-6 rounded-lg shadow`}>
              <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Měsíční přehled</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className={isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}>
                    <tr>
                      <th className={`px-3 py-2 text-left text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-500'} uppercase`}>Měsíc</th>
                      <th className={`px-3 py-2 text-center text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-500'} uppercase`}>Hlavní provize</th>
                      <th className={`px-3 py-2 text-center text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-500'} uppercase`}>Strategické</th>
                      <th className={`px-3 py-2 text-center text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-500'} uppercase`}>Cashbacky</th>
                      <th className={`px-3 py-2 text-center text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-500'} uppercase`}>Celkem</th>
                    </tr>
                  </thead>
                  <tbody className={`divide-y ${isDarkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                    {adminStats.monthlyStats.map((month) => (
                      <tr key={month.month} className={isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}>
                        <td className={`px-3 py-2 text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                          {new Date(month.month + '-01').toLocaleDateString('cs-CZ', { year: 'numeric', month: 'long' })}
                        </td>
                        <td className={`px-3 py-2 text-sm text-center ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{month.regular || 0}</td>
                        <td className={`px-3 py-2 text-sm text-center ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{month.strategic || 0}</td>
                        <td className="px-3 py-2 text-sm text-center text-red-600">{month.cashbacks || 0}</td>
                        <td className={`px-3 py-2 text-sm text-center font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{(month.regular + month.strategic + month.cashbacks) || 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'overview' && (
        <div>
          {/* Header s výběrem měsíce */}
          <div className="mb-6 flex items-center justify-between flex-wrap gap-4">
            <h2 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              👥 Celkový přehled k výplatě
            </h2>
            <div className="flex items-center gap-3">
              <label className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Měsíc:</label>
              <input
                type="month"
                value={overviewMonth}
                onChange={(e) => setOverviewMonth(e.target.value)}
                className={`px-3 py-2 rounded border text-sm ${
                  isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                }`}
              />
              <button
                onClick={() => fetchOverviewData(overviewMonth)}
                disabled={loadingOverview}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded transition disabled:opacity-50"
              >
                {loadingOverview ? 'Načítám...' : '🔄 Obnovit'}
              </button>
            </div>
          </div>

          {loadingOverview ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-10 w-10 border-4 border-blue-500 border-t-transparent mr-3"></div>
              <span className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>Načítám data...</span>
            </div>
          ) : overviewData.length === 0 ? (
            <div className={`text-center py-12 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Žádná data pro zvolený měsíc.
            </div>
          ) : (
            <>
              {/* Souhrn - celkem k výplatě */}
              <div className={`mb-6 p-4 rounded-lg border-l-4 border-green-500 ${isDarkMode ? 'bg-gray-800' : 'bg-green-50'}`}>
                <div className="flex justify-between items-center">
                  <span className={`font-semibold ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>
                    Celkem k výplatě (všichni)
                  </span>
                  <span className="text-green-600 font-bold text-xl">
                    {new Intl.NumberFormat('cs-CZ', { minimumFractionDigits: 0, maximumFractionDigits: 1 }).format(
                      overviewData.reduce((s, u) => s + u.toPay, 0)
                    )} Kč
                  </span>
                </div>
              </div>

              {/* Tabulka */}
              <div className={`rounded-lg shadow overflow-hidden border ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead className={isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}>
                      <tr>
                        <th className={`px-4 py-3 text-left text-xs font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-600'} uppercase tracking-wider`}>Jméno</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-blue-500 uppercase tracking-wider">Hlavní provize</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-purple-500 uppercase tracking-wider">Strategické</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-red-500 uppercase tracking-wider">Cashback</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-orange-500 uppercase tracking-wider">Cashback strat.</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-yellow-500 uppercase tracking-wider">Zádržné</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-green-500 uppercase tracking-wider">K výplatě</th>
                      </tr>
                    </thead>
                    <tbody className={`divide-y ${isDarkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                      {overviewData.map((u) => (
                        <tr key={u.userId} className={isDarkMode ? 'bg-gray-800 hover:bg-gray-700' : 'bg-white hover:bg-gray-50'}>
                          <td className={`px-4 py-3 font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{u.name}</td>
                          <td className={`px-4 py-3 text-right text-sm ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                            {new Intl.NumberFormat('cs-CZ', { minimumFractionDigits: 0, maximumFractionDigits: 1 }).format(u.main)} Kč
                          </td>
                          <td className={`px-4 py-3 text-right text-sm ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`}>
                            {new Intl.NumberFormat('cs-CZ', { minimumFractionDigits: 0, maximumFractionDigits: 1 }).format(u.strategic)} Kč
                          </td>
                          <td className={`px-4 py-3 text-right text-sm ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>
                            {new Intl.NumberFormat('cs-CZ', { minimumFractionDigits: 0, maximumFractionDigits: 1 }).format(u.cashback)} Kč
                          </td>
                          <td className={`px-4 py-3 text-right text-sm ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`}>
                            {new Intl.NumberFormat('cs-CZ', { minimumFractionDigits: 0, maximumFractionDigits: 1 }).format(u.cashbackStrategic)} Kč
                          </td>
                          <td className={`px-4 py-3 text-right text-sm ${isDarkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>
                            -{new Intl.NumberFormat('cs-CZ', { minimumFractionDigits: 0, maximumFractionDigits: 1 }).format(u.retention)} Kč
                          </td>
                          <td className="px-4 py-3 text-right">
                            <span className={`font-bold text-sm ${u.toPay >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {new Intl.NumberFormat('cs-CZ', { minimumFractionDigits: 0, maximumFractionDigits: 1 }).format(u.toPay)} Kč
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className={isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}>
                      <tr>
                        <td className={`px-4 py-3 font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Celkem</td>
                        <td className={`px-4 py-3 text-right font-bold text-sm ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                          {new Intl.NumberFormat('cs-CZ', { minimumFractionDigits: 0, maximumFractionDigits: 1 }).format(overviewData.reduce((s, u) => s + u.main, 0))} Kč
                        </td>
                        <td className={`px-4 py-3 text-right font-bold text-sm ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`}>
                          {new Intl.NumberFormat('cs-CZ', { minimumFractionDigits: 0, maximumFractionDigits: 1 }).format(overviewData.reduce((s, u) => s + u.strategic, 0))} Kč
                        </td>
                        <td className={`px-4 py-3 text-right font-bold text-sm ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>
                          {new Intl.NumberFormat('cs-CZ', { minimumFractionDigits: 0, maximumFractionDigits: 1 }).format(overviewData.reduce((s, u) => s + u.cashback, 0))} Kč
                        </td>
                        <td className={`px-4 py-3 text-right font-bold text-sm ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`}>
                          {new Intl.NumberFormat('cs-CZ', { minimumFractionDigits: 0, maximumFractionDigits: 1 }).format(overviewData.reduce((s, u) => s + u.cashbackStrategic, 0))} Kč
                        </td>
                        <td className={`px-4 py-3 text-right font-bold text-sm ${isDarkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>
                          -{new Intl.NumberFormat('cs-CZ', { minimumFractionDigits: 0, maximumFractionDigits: 1 }).format(overviewData.reduce((s, u) => s + u.retention, 0))} Kč
                        </td>
                        <td className="px-4 py-3 text-right font-bold text-green-600">
                          {new Intl.NumberFormat('cs-CZ', { minimumFractionDigits: 0, maximumFractionDigits: 1 }).format(overviewData.reduce((s, u) => s + u.toPay, 0))} Kč
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {activeTab === 'bmsl' && (
        <div>
          <div className="mb-4 flex items-center justify-between">
            <h2 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>BMSL přehled podle uživatelů</h2>
            <div className="flex items-center gap-4">
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className={`border rounded px-3 py-2 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
              >
                {Array.from({length: 12}, (_, i) => {
                  const _base = new Date();
                  const date = new Date(_base.getFullYear(), _base.getMonth() - i, 1);
                  const monthStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
                  return (
                    <option key={monthStr} value={monthStr}>
                      {date.toLocaleDateString('cs-CZ', { year: 'numeric', month: 'long' })}
                    </option>
                  );
                })}
              </select>
              <button
                onClick={() => fetchBmslData(selectedMonth)}
                disabled={loading}
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? 'Načítání...' : 'Obnovit'}
              </button>
              <button
                onClick={exportBmslToExcel}
                disabled={!bmslData || bmslData.length === 0}
                className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
                title="Exportovat IČ a firmy do Excelu"
              >
                📥 Export IČ do Excelu
              </button>
            </div>
          </div>

          {/* Celkový součet BMSL */}
          {bmslData.length > 0 && (
            <div className={`mb-4 p-4 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-blue-50'} border ${isDarkMode ? 'border-gray-600' : 'border-blue-200'}`}>
              <div className="flex items-center justify-between">
                <span className={`text-lg font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                  Celkové BMSL za měsíc:
                </span>
                <span className={`text-2xl font-bold ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
                  {bmslData.reduce((sum, user) => sum + (user.totalBmsl || 0), 0).toLocaleString('cs-CZ')} Kč
                </span>
              </div>
              <div className={`mt-2 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Celkem {bmslData.filter(u => u.companyCount > 0).length} obchodníků zobchodovalo {bmslData.reduce((sum, user) => sum + (user.companyCount || 0), 0)} firem
              </div>
            </div>
          )}

          <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow`}>
            {bmslData.length === 0 ? (
              <div className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {loading ? 'Načítání dat...' : 'Žádná data pro vybraný měsíc.'}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className={isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}>
                    <tr>
                      <th className={`px-4 py-3 text-left text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-500'} uppercase`}>Obchodník</th>
                      <th className={`px-4 py-3 text-right text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-500'} uppercase`}>Počet firem</th>
                      <th className={`px-4 py-3 text-right text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-500'} uppercase`}>Celkové BMSL</th>
                      <th className={`px-4 py-3 text-right text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-500'} uppercase`}>Průměrné BMSL</th>
                    </tr>
                  </thead>
                  <tbody className={`divide-y ${isDarkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                    {bmslData.map((user) => (
                      <React.Fragment key={user.userName}>
                        <tr 
                          className={`${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'} cursor-pointer`}
                          onClick={() => toggleUserExpansion(user.userName)}
                        >
                          <td className={`px-4 py-3 text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            <div className="flex items-center">
                              <span className={`mr-2 transform transition-transform ${expandedUsers.has(user.userName) ? 'rotate-90' : ''}`}>
                                ▶
                              </span>
                              {user.userName || 'Neznámý uživatel'}
                            </div>
                          </td>
                          <td className={`px-4 py-3 text-sm text-right ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            {user.companyCount || 0}
                          </td>
                          <td className={`px-4 py-3 text-sm text-right font-bold ${user.totalBmsl > 0 ? 'text-green-600' : (isDarkMode ? 'text-gray-400' : 'text-gray-500')}`}>
                            {user.totalBmsl ? user.totalBmsl.toLocaleString('cs-CZ') : 0} Kč
                          </td>
                          <td className={`px-4 py-3 text-sm text-right ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            {user.averageBmsl ? user.averageBmsl.toLocaleString('cs-CZ') : 0} Kč
                          </td>
                        </tr>
                        {expandedUsers.has(user.userName) && user.companies && user.companies.length > 0 && (
                          <tr>
                            <td colSpan="4" className={`px-4 py-2 ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
                              <div className="ml-6">
                                <h4 className={`text-xs font-semibold mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                                  Zobchodované firmy ({user.companies.length}):
                                </h4>
                                <div className="space-y-1">
                                  {user.companies.map((company, index) => (
                                    <div key={index} className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} flex justify-between items-center`}>
                                      <span className="flex items-center gap-2">
                                        <strong>{company.name}</strong> ({company.ico})
                                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                          company.type === 'upsell' 
                                            ? isDarkMode ? 'bg-purple-900 text-purple-200' : 'bg-purple-100 text-purple-800'
                                            : isDarkMode ? 'bg-blue-900 text-blue-200' : 'bg-blue-100 text-blue-800'
                                        }`}>
                                          {company.type === 'upsell' ? 'Upsell' : 'Hlavní'}
                                        </span>
                                      </span>
                                      <span className="flex gap-4">
                                        <span>Zobchodováno: {new Date(company.closedDate).toLocaleDateString('cs-CZ')}</span>
                                        <span className="font-semibold text-green-600">{company.bmsl} Kč</span>
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className={`mt-4 p-4 ${isDarkMode ? 'bg-gray-800' : 'bg-blue-50'} rounded-lg`}>
            <h3 className={`text-sm font-semibold mb-2 ${isDarkMode ? 'text-blue-400' : 'text-blue-800'}`}>ℹ️ Informace o výpočtu</h3>
            <ul className={`text-xs space-y-1 ${isDarkMode ? 'text-gray-400' : 'text-blue-700'}`}>
              <li>• Zobrazují se všichni obchodníci (i s nulovými výsledky)</li>
              <li>• Zahrnují se hlavní firmy i forecast upselly s hodnotou forecast 100%</li>
              <li>• Filtrováno podle měsíce "Zobchodováno"</li>
              <li>• Započítávají se pouze firmy/upselly ve vlastnictví daného obchodníka</li>
              <li>• Klikněte na řádek pro zobrazení detailu zobchodovaných firem a upsellů</li>
              <li>• Data se načítají z hlavní databáze firem a upsellů, ne z komisního systému</li>
            </ul>
          </div>
        </div>
      )}

      {activeTab === 'coefficients' && (
        <div>
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h2 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'} mb-1`}>Koeficienty pro Hlavní provize</h2>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Zobrazeni všichni uživatelé. Klikněte na koeficient nebo poznámku pro úpravu. Výchozí hodnota je 1.0
              </p>
            </div>
            <div className="flex items-center gap-3">
              <label className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Měsíc GP:</label>
              <select
                value={coefficientsMonth}
                onChange={(e) => setCoefficientsMonth(e.target.value)}
                className={`border rounded px-3 py-2 text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
              >
                {Array.from({length: 24}, (_, i) => {
                  const now = new Date();
                  const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
                  const monthStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
                  return (
                    <option key={monthStr} value={monthStr}>
                      {date.toLocaleDateString('cs-CZ', { year: 'numeric', month: 'long' })}
                    </option>
                  );
                })}
              </select>
            </div>
          </div>

          {!allUsers || allUsers.length === 0 ? (
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow p-8 text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {loading ? 'Načítání uživatelů...' : 'Žádní uživatelé nebyli nalezeni.'}
            </div>
          ) : (
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow overflow-x-auto`}>
              <table className="min-w-full divide-y divide-gray-200">
                <thead className={isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}>
                  <tr>
                    <th className={`px-4 py-3 text-left text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-500'} uppercase`}>Uživatel</th>
                    <th className={`px-4 py-3 text-right text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-500'} uppercase w-44`}>GP hlavní provize</th>
                    <th className={`px-4 py-3 text-center text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-500'} uppercase w-32`}>Koeficient</th>
                    <th className={`px-4 py-3 text-left text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-500'} uppercase`}>Poznámka</th>
                    <th className={`px-4 py-3 text-center text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-500'} uppercase w-40`}>Poslední aktualizace</th>
                  </tr>
                </thead>
                <tbody className={`divide-y ${isDarkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                  {allUsers.map((user) => {
                  const coeff = userCoefficients.find(c => c.userId && (c.userId._id === user._id || c.userId === user._id));
                  const isEditingCoeff = editingCoefficientUserId === user._id;
                  const isEditingDesc = editingDescriptionUserId === user._id;
                  
                  return (
                    <tr key={user._id} className={isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}>
                      <td className={`px-4 py-3 text-sm font-medium ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                        {user.name}
                      </td>
                      <td className={`px-4 py-3 text-right text-sm font-semibold ${
                        (gpByUser[user._id] || 0) > 0
                          ? 'text-green-500'
                          : isDarkMode ? 'text-gray-500' : 'text-gray-400'
                      }`}>
                        {(gpByUser[user._id] || 0).toLocaleString('cs-CZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} Kč
                      </td>
                      <td className="px-4 py-3 text-center">
                        {isEditingCoeff ? (
                          <input
                            type="text"
                            inputMode="decimal"
                            value={tempCoefficientValue}
                            onChange={(e) => setTempCoefficientValue(e.target.value)}
                            onBlur={() => handleSaveCoefficientInline(user._id, coeff?._id)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleSaveCoefficientInline(user._id, coeff?._id);
                              if (e.key === 'Escape') { setEditingCoefficientUserId(null); setTempCoefficientValue(''); }
                            }}
                            autoFocus
                            className={`w-20 px-2 py-1 text-center border rounded ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
                            placeholder="1.0"
                          />
                        ) : (
                          <button
                            onClick={() => {
                              setEditingCoefficientUserId(user._id);
                              setTempCoefficientValue((coeff?.coefficient || 1.0).toString());
                            }}
                            className={`px-3 py-1 rounded font-bold ${isDarkMode ? 'text-white hover:bg-gray-600' : 'text-gray-900 hover:bg-gray-100'}`}
                          >
                            {coeff?.coefficient || 1.0}
                          </button>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {isEditingDesc ? (
                          <input
                            type="text"
                            value={tempDescriptionValue}
                            onChange={(e) => setTempDescriptionValue(e.target.value)}
                            onBlur={(e) => {
                              e.preventDefault();
                              handleSaveDescriptionInline(user._id, coeff?._id);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleSaveDescriptionInline(user._id, coeff?._id);
                              }
                              if (e.key === 'Escape') { 
                                e.preventDefault();
                                setEditingDescriptionUserId(null); 
                                setTempDescriptionValue(''); 
                              }
                            }}
                            autoFocus
                            className={`w-full px-2 py-1 border rounded text-sm ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
                            placeholder="Přidat poznámku..."
                          />
                        ) : (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              setEditingDescriptionUserId(user._id);
                              setTempDescriptionValue(coeff?.description || '');
                            }}
                            className={`text-left w-full px-2 py-1 rounded text-sm ${isDarkMode ? 'text-gray-300 hover:bg-gray-600' : 'text-gray-700 hover:bg-gray-100'}`}
                          >
                            {coeff?.description || <span className={isDarkMode ? 'text-gray-500' : 'text-gray-400'}>Klikněte pro přidání poznámky...</span>}
                          </button>
                        )}
                      </td>
                      <td className={`px-4 py-3 text-sm text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        {coeff?.updatedAt ? new Date(coeff.updatedAt).toLocaleString('cs-CZ', { 
                          year: 'numeric', 
                          month: '2-digit', 
                          day: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit'
                        }) : '-'}
                      </td>
                    </tr>
                  );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === 'strategic-coefficients' && (
        <div>
          <div className="mb-4">
            <h2 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'} mb-2`}>Koeficienty pro Strategické produkty</h2>
            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Zobrazeni všichni uživatelé. Klikněte na koeficient nebo poznámku pro úpravu. Výchozí hodnota je 1.0
            </p>
          </div>

          {!allUsers || allUsers.length === 0 ? (
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow p-8 text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {loading ? 'Načítání uživatelů...' : 'Žádní uživatelé nebyli nalezeni.'}
            </div>
          ) : (
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow overflow-x-auto`}>
              <table className="min-w-full divide-y divide-gray-200">
                <thead className={isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}>
                  <tr>
                    <th className={`px-4 py-3 text-left text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-500'} uppercase`}>Uživatel</th>
                    <th className={`px-4 py-3 text-center text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-500'} uppercase w-32`}>Strategický koeficient</th>
                    <th className={`px-4 py-3 text-left text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-500'} uppercase`}>Poznámka</th>
                    <th className={`px-4 py-3 text-center text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-500'} uppercase w-40`}>Poslední aktualizace</th>
                  </tr>
                </thead>
                <tbody className={`divide-y ${isDarkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                  {allUsers.map((user) => {
                  const coeff = strategicCoefficients.find(c => c.userId && (c.userId._id === user._id || c.userId === user._id));
                  const isEditingCoeff = editingStrategicCoefficientUserId === user._id;
                  const isEditingDesc = editingStrategicDescriptionUserId === user._id;
                  
                  return (
                    <tr key={user._id} className={isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}>
                      <td className={`px-4 py-3 text-sm font-medium ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                        {user.name}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {isEditingCoeff ? (
                          <input
                            type="text"
                            inputMode="decimal"
                            value={tempStrategicCoefficientValue}
                            onChange={(e) => setTempStrategicCoefficientValue(e.target.value)}
                            onBlur={() => handleSaveStrategicCoefficientInline(user._id, coeff?._id)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleSaveStrategicCoefficientInline(user._id, coeff?._id);
                              if (e.key === 'Escape') { setEditingStrategicCoefficientUserId(null); setTempStrategicCoefficientValue(''); }
                            }}
                            autoFocus
                            className={`w-20 px-2 py-1 text-center border rounded ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
                            placeholder="1.0"
                          />
                        ) : (
                          <button
                            onClick={() => {
                              setEditingStrategicCoefficientUserId(user._id);
                              setTempStrategicCoefficientValue((coeff?.coefficient || 1.0).toString());
                            }}
                            className={`px-3 py-1 rounded font-bold ${isDarkMode ? 'text-white hover:bg-gray-600' : 'text-gray-900 hover:bg-gray-100'}`}
                          >
                            {coeff?.coefficient || 1.0}
                          </button>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {isEditingDesc ? (
                          <input
                            type="text"
                            value={tempStrategicDescriptionValue}
                            onChange={(e) => setTempStrategicDescriptionValue(e.target.value)}
                            onBlur={() => handleSaveStrategicDescriptionInline(user._id, coeff?._id)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleSaveStrategicDescriptionInline(user._id, coeff?._id);
                              if (e.key === 'Escape') { setEditingStrategicDescriptionUserId(null); setTempStrategicDescriptionValue(''); }
                            }}
                            autoFocus
                            className={`w-full px-2 py-1 border rounded text-sm ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
                            placeholder="Přidat poznámku..."
                          />
                        ) : (
                          <button
                            onClick={() => {
                              setEditingStrategicDescriptionUserId(user._id);
                              setTempStrategicDescriptionValue(coeff?.description || '');
                            }}
                            className={`text-left w-full px-2 py-1 rounded text-sm ${isDarkMode ? 'text-gray-300 hover:bg-gray-600' : 'text-gray-700 hover:bg-gray-100'}`}
                          >
                            {coeff?.description || <span className={isDarkMode ? 'text-gray-500' : 'text-gray-400'}>Klikněte pro přidání poznámky...</span>}
                          </button>
                        )}
                      </td>
                      <td className={`px-4 py-3 text-sm text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        {coeff?.updatedAt ? new Date(coeff.updatedAt).toLocaleString('cs-CZ', { 
                          year: 'numeric', 
                          month: '2-digit', 
                          day: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit'
                        }) : '-'}
                      </td>
                    </tr>
                  );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === 'retention' && (
        <div>
          <h2 className={`text-xl font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Správa zádržného fondu</h2>
          
          {/* Hromadné nastavení */}
          <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} p-6 rounded-lg shadow mb-6`}>
            <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Hromadné nastavení</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                  Procento zádržného (%)
                </label>
                <input
                  type="text"
                  value={bulkRetentionSettings.retentionPercentage}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9.,]/g, '').replace(',', '.');
                    setBulkRetentionSettings({...bulkRetentionSettings, retentionPercentage: value});
                  }}
                  className={`w-full px-3 py-2 border rounded ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                  placeholder="Např. 10"
                />
              </div>
              <div>
                <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                  Maximální fond (Kč)
                </label>
                <input
                  type="text"
                  value={bulkRetentionSettings.maxRetentionAmount}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9]/g, '');
                    setBulkRetentionSettings({...bulkRetentionSettings, maxRetentionAmount: value});
                  }}
                  className={`w-full px-3 py-2 border rounded ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                  placeholder="Např. 50000"
                />
              </div>
              <div className="flex items-end">
                <button
                  onClick={applyBulkRetentionSettings}
                  className="w-full bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700"
                >
                  Aplikovat na všechny
                </button>
              </div>
            </div>
          </div>

          {/* Nastavení pro jednotlivé uživatele */}
          <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} p-6 rounded-lg shadow`}>
            <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Nastavení pro jednotlivé uživatele</h3>
            
            {loading ? (
              <div className="text-center py-8">Načítání...</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                    <tr>
                      <th className={`px-3 py-3 text-left text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-500'} uppercase`}>Uživatel</th>
                      <th className={`px-3 py-3 text-center text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-500'} uppercase`}>Procento (%)</th>
                      <th className={`px-3 py-3 text-center text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-500'} uppercase`}>Max. fond (Kč)</th>
                      <th className={`px-3 py-3 text-center text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-500'} uppercase`}>Aktivní</th>
                      <th className={`px-3 py-3 text-center text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-500'} uppercase`}>Akce</th>
                    </tr>
                  </thead>
                  <tbody className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} divide-y ${isDarkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                    {allUsers.map((user) => {
                      const userSettings = retentionSettings.find(s => s.userId?._id === user._id) || {};
                      return (
                        <tr key={user._id} className={`${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}`}>
                          <td className={`px-3 py-2 text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            {user.name}
                          </td>
                          <td className="px-3 py-2 text-center">
                            <input
                              key={`retention-percentage-${user._id}-${userSettings.retentionPercentage || 0}`}
                              type="text"
                              defaultValue={userSettings.retentionPercentage || 0}
                              className={`w-20 px-2 py-1 text-sm border rounded text-center ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                              onChange={(e) => {
                                const value = e.target.value.replace(/[^0-9.,]/g, '').replace(',', '.');
                                e.target.value = value; // Aktualizuj zobrazení
                                // Aktualizuj local stav pro okamžité zobrazení
                                setRetentionSettings(prev => {
                                  const updated = [...prev];
                                  const index = updated.findIndex(s => s.userId?._id === user._id);
                                  if (index >= 0) {
                                    updated[index] = { ...updated[index], retentionPercentage: Number(value || 0) };
                                  } else {
                                    updated.push({
                                      userId: user,
                                      retentionPercentage: Number(value || 0),
                                      maxRetentionAmount: 0,
                                      isActive: true
                                    });
                                  }
                                  return updated;
                                });
                              }}
                              onBlur={(e) => {
                                const value = e.target.value.replace(/[^0-9.,]/g, '').replace(',', '.');
                                updateRetentionSettings(user._id, {
                                  retentionPercentage: Number(value || 0),
                                  maxRetentionAmount: userSettings.maxRetentionAmount || 0,
                                  isActive: userSettings.isActive !== false
                                });
                              }}
                            />
                          </td>
                          <td className="px-3 py-2 text-center">
                            <input
                              key={`retention-max-${user._id}-${userSettings.maxRetentionAmount || 0}`}
                              type="text"
                              defaultValue={userSettings.maxRetentionAmount || 0}
                              className={`w-24 px-2 py-1 text-sm border rounded text-center ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                              onChange={(e) => {
                                const value = e.target.value.replace(/[^0-9]/g, '');
                                e.target.value = value;
                                setRetentionSettings(prev => {
                                  const updated = [...prev];
                                  const index = updated.findIndex(s => s.userId?._id === user._id);
                                  if (index >= 0) {
                                    updated[index] = { ...updated[index], maxRetentionAmount: Number(value || 0) };
                                  } else {
                                    updated.push({
                                      userId: user,
                                      retentionPercentage: 0,
                                      maxRetentionAmount: Number(value || 0),
                                      isActive: true
                                    });
                                  }
                                  return updated;
                                });
                              }}
                              onBlur={(e) => {
                                const value = e.target.value.replace(/[^0-9]/g, '');
                                updateRetentionSettings(user._id, {
                                  retentionPercentage: userSettings.retentionPercentage || 0,
                                  maxRetentionAmount: Number(value || 0),
                                  isActive: userSettings.isActive !== false
                                });
                              }}
                            />
                          </td>
                          <td className="px-3 py-2 text-center">
                            <input
                              key={`retention-active-${user._id}-${userSettings.isActive !== false}`}
                              type="checkbox"
                              defaultChecked={userSettings.isActive !== false}
                              className="rounded"
                              onChange={(e) => {
                                const checked = e.target.checked;
                                updateRetentionSettings(user._id, {
                                  retentionPercentage: userSettings.retentionPercentage || 0,
                                  maxRetentionAmount: userSettings.maxRetentionAmount || 0,
                                  isActive: checked
                                });
                              }}
                            />
                          </td>
                          <td className="px-3 py-2 text-center">
                            <button
                              onClick={() => updateRetentionSettings(user._id, {
                                retentionPercentage: userSettings.retentionPercentage || 0,
                                maxRetentionAmount: userSettings.maxRetentionAmount || 0,
                                isActive: userSettings.isActive !== false
                              })}
                              className="text-blue-600 hover:text-blue-800 text-sm"
                            >
                              💾 Uložit
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'retention-overview' && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Zádržné - správa</h2>
            <button
              onClick={fetchRetentionOverview}
              disabled={loadingRetentionOverview}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50 text-sm"
            >
              {loadingRetentionOverview ? 'Načítání...' : '🔄 Obnovit'}
            </button>
          </div>

          <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow overflow-hidden`}>
            {loadingRetentionOverview ? (
              <div className="text-center py-12">Načítání dat...</div>
            ) : retentionOverviewData.length === 0 ? (
              <div className={`text-center py-12 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Žádná data o zádržném. Nejprve přepočítejte zádržné v záložce Nástroje.
              </div>
            ) : (() => {
              const todayMonth = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`; })();
              const prevMonth = (() => { const d = new Date(); d.setMonth(d.getMonth()-1); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`; })();
              const todayLabel = new Date(todayMonth + '-01').toLocaleDateString('cs-CZ', { month: 'long', year: 'numeric' });
              const prevLabel = new Date(prevMonth + '-01').toLocaleDateString('cs-CZ', { month: 'long', year: 'numeric' });
              return (
                <table className="w-full">
                  <thead className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                    <tr>
                      <th className={`px-4 py-3 text-left text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-500'} uppercase`}>Obchodník</th>
                      <th className={`px-4 py-3 text-right text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-500'} uppercase`}>Aktuální fond (Kč)</th>
                      <th className={`px-4 py-3 text-right text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-500'} uppercase`}>Zádržné posl. měs. (Kč)</th>
                      <th className={`px-4 py-3 text-right text-xs font-medium text-green-600 uppercase`}>Uvolněno {todayLabel}</th>
                      <th className={`px-4 py-3 text-right text-xs font-medium text-green-600 uppercase`}>Uvolněno {prevLabel}</th>
                      <th className={`px-4 py-3 text-center text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-500'} uppercase`}>Poslední měsíc</th>
                      <th className={`px-4 py-3 text-center text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-500'} uppercase`}>Detail</th>
                    </tr>
                  </thead>
                  <tbody className={`divide-y ${isDarkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                    {retentionOverviewData.map(record => {
                      const uid = record.userId?._id || record.userId;
                      const userName = record.userId?.name || 'Neznámý';
                      const isExpanded = expandedRetentionUsers.has(uid);
                      const history = retentionUserHistory[uid] || [];
                      const histLoading = loadingRetentionHistory[uid];
                      const allRecords = retentionAllRecordsByUser[uid] || [];
                      const thisMonthRec = allRecords.find(r => r.month === todayMonth);
                      const lastMonthRec = allRecords.find(r => r.month === prevMonth);
                      const thisMonthTooltip = formatReleasedItemsTooltip(thisMonthRec?.releasedItems);
                      const lastMonthTooltip = formatReleasedItemsTooltip(lastMonthRec?.releasedItems);
                      return (
                        <React.Fragment key={uid}>
                          <tr
                            className={`cursor-pointer ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'} transition-colors`}
                            onClick={() => toggleRetentionUser(uid)}
                          >
                            <td className={`px-4 py-3 text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                              <span className="mr-2">{isExpanded ? '▼' : '▶'}</span>
                              {userName}
                            </td>
                            <td className={`px-4 py-3 text-sm text-right font-semibold ${record.currentFundBalance > 0 ? 'text-yellow-600' : (isDarkMode ? 'text-gray-300' : 'text-gray-700')}`}>
                              {(record.currentFundBalance || 0).toLocaleString('cs-CZ')} Kč
                            </td>
                            <td className={`px-4 py-3 text-sm text-right ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                              {(record.retentionAmount || 0).toLocaleString('cs-CZ')} Kč
                            </td>
                            <td className="px-4 py-3 text-sm text-right" onClick={e => e.stopPropagation()}>
                              {(thisMonthRec?.releasedAmount || 0) > 0 ? (
                                <span className="text-green-600 font-semibold cursor-help" title={thisMonthTooltip}>
                                  {thisMonthRec.releasedAmount.toLocaleString('cs-CZ')} Kč
                                </span>
                              ) : (
                                <span className={isDarkMode ? 'text-gray-600' : 'text-gray-400'}>0 Kč</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm text-right" onClick={e => e.stopPropagation()}>
                              {(lastMonthRec?.releasedAmount || 0) > 0 ? (
                                <span className="text-green-600 font-semibold cursor-help" title={lastMonthTooltip}>
                                  {lastMonthRec.releasedAmount.toLocaleString('cs-CZ')} Kč
                                </span>
                              ) : (
                                <span className={isDarkMode ? 'text-gray-600' : 'text-gray-400'}>0 Kč</span>
                              )}
                            </td>
                            <td className={`px-4 py-3 text-sm text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              {record.month
                                ? new Date(record.month + '-01').toLocaleDateString('cs-CZ', { year: 'numeric', month: 'long' })
                                : '-'}
                            </td>
                            <td className="px-4 py-3 text-center" onClick={e => e.stopPropagation()}>
                              <button
                                onClick={() => toggleRetentionUser(uid)}
                                className={`text-xs px-2 py-1 rounded ${isDarkMode ? 'bg-gray-600 text-gray-300 hover:bg-gray-500' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                              >
                                {isExpanded ? 'Skrýt' : 'Rozbalit'}
                              </button>
                            </td>
                          </tr>
                          {isExpanded && (
                            <tr className={isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}>
                              <td colSpan={7} className="px-4 py-4">
                                {histLoading ? (
                                  <div className="text-center py-4">Načítání histórie...</div>
                                ) : history.length === 0 ? (
                                  <div className={`text-center py-4 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Žádná data</div>
                                ) : (
                                  <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                      <thead>
                                        <tr className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>
                                          <th className="px-3 py-2 text-left font-medium uppercase text-xs">Měsíc</th>
                                          <th className="px-3 py-2 text-right font-medium uppercase text-xs">Provize celkem (Kč)</th>
                                          <th className="px-3 py-2 text-right font-medium uppercase text-xs">Zádržné (Kč)</th>
                                          <th className="px-3 py-2 text-right font-medium uppercase text-xs">Uvolněno (Kč)</th>
                                          <th className="px-3 py-2 text-left font-medium uppercase text-xs">Položky uvolnění</th>
                                          <th className="px-3 py-2 text-right font-medium uppercase text-xs">Stav fondu (Kč)</th>
                                        </tr>
                                      </thead>
                                      <tbody className={`divide-y ${isDarkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                                        {history.map(h => (
                                          <tr key={h.month} className={isDarkMode ? 'hover:bg-gray-800' : 'hover:bg-white'}>
                                            <td className={`px-3 py-2 font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                                              {new Date(h.month + '-01').toLocaleDateString('cs-CZ', { year: 'numeric', month: 'long' })}
                                            </td>
                                            <td className={`px-3 py-2 text-right ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                              {(h.totalCommission || 0).toLocaleString('cs-CZ')} Kč
                                            </td>
                                            <td className={`px-3 py-2 text-right font-semibold ${h.retentionAmount > 0 ? 'text-orange-500' : (isDarkMode ? 'text-gray-400' : 'text-gray-500')}`}>
                                              {(h.retentionAmount || 0).toLocaleString('cs-CZ')} Kč
                                            </td>
                                            <td className={`px-3 py-2 text-right ${h.releasedAmount > 0 ? 'text-green-600 font-semibold' : (isDarkMode ? 'text-gray-500' : 'text-gray-400')}`}>
                                              {(h.releasedAmount || 0).toLocaleString('cs-CZ')} Kč
                                            </td>
                                            <td className={`px-3 py-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                              {h.releasedItems && h.releasedItems.length > 0 ? (
                                                <ul className="space-y-0.5">
                                                  {h.releasedItems.map((item, idx) => (
                                                    <li key={idx} className="flex items-center gap-1 text-xs">
                                                      <span className={`px-1 rounded ${item.commissionType === 'strategic' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                                                        {item.commissionType === 'strategic' ? 'strat.' : 'hl.'}
                                                      </span>
                                                      <span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>{item.companyName || '?'}</span>
                                                      <span className="text-green-600 font-medium ml-1">{(item.amount || 0).toLocaleString('cs-CZ')} Kč</span>
                                                      <button
                                                        onClick={() => deleteReleaseItem(uid, h.month, item._id, `${item.companyName || '?'} – ${(item.amount || 0).toLocaleString('cs-CZ')} Kč`)}
                                                        title="Vrátit zpět do zádržného"
                                                        className="ml-1 text-red-400 hover:text-red-600 transition-colors flex-shrink-0"
                                                      >✕</button>
                                                    </li>
                                                  ))}
                                                </ul>
                                              ) : (
                                                <span className={isDarkMode ? 'text-gray-600' : 'text-gray-400'}>-</span>
                                              )}
                                            </td>
                                            <td className={`px-3 py-2 text-right font-bold ${h.currentFundBalance > 0 ? 'text-yellow-600' : (isDarkMode ? 'text-gray-300' : 'text-gray-700')}`}>
                                              {(h.currentFundBalance || 0).toLocaleString('cs-CZ')} Kč
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                )}
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              );
            })()}
          </div>
        </div>
      )}

      {activeTab === 'support-commissions' && (
        <div>
          <div className="mb-5">
            <h2 className={`text-xl font-semibold mb-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>⚙️ Nastavení provizí Podpory</h2>
            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Definujte koeficient GP a pevnou hodnotu odměny dle typu požadavku. Provize = GP firmy × Koeficient GP + Hodnota dle typu.
            </p>
          </div>

          <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow p-6 mb-6`}>
            {/* Koeficient GP */}
            <div className="mb-6">
              <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                Koeficient GP (% z GP firmy)
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="number"
                  min="0"
                  max="1"
                  step="0.001"
                  value={supportSettings.gpCoefficient}
                  onChange={e => setSupportSettings({ ...supportSettings, gpCoefficient: parseFloat(e.target.value) || 0 })}
                  className={`w-36 px-3 py-2 rounded border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                  placeholder="0.1"
                />
                <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  = {((supportSettings.gpCoefficient || 0) * 100).toFixed(2)} % z GP firmy
                </span>
              </div>
              <p className={`mt-1 text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Zadejte jako desetinné číslo (0.1 = 10 %)</p>
            </div>

            {/* Pevná hodnota dle typu požadavku */}
            <div className="mb-6">
              <label className={`block text-sm font-semibold mb-3 ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                Pevná hodnota odměny dle typu požadavku (Kč)
              </label>
              <div className="space-y-3">
                {supportSettings.requestTypeValues.map((rtv, idx) => (
                  <div key={rtv.typeName} className="flex items-center gap-3">
                    <span className={`w-44 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{rtv.typeName}</span>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={rtv.value}
                      onChange={e => {
                        const updated = supportSettings.requestTypeValues.map((r, i) =>
                          i === idx ? { ...r, value: parseFloat(e.target.value) || 0 } : r
                        );
                        setSupportSettings({ ...supportSettings, requestTypeValues: updated });
                      }}
                      className={`w-36 px-3 py-2 rounded border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                    />
                    <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Kč / požadavek</span>
                    <label className="flex items-center gap-2 ml-4 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={rtv.includeGp !== false}
                        onChange={e => {
                          const updated = supportSettings.requestTypeValues.map((r, i) =>
                            i === idx ? { ...r, includeGp: e.target.checked } : r
                          );
                          setSupportSettings({ ...supportSettings, requestTypeValues: updated });
                        }}
                        className="w-4 h-4 accent-blue-600"
                      />
                      <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Zahrnovat GP</span>
                    </label>
                  </div>
                ))}
              </div>
            </div>

            <button
              onClick={saveSupportSettings}
              disabled={supportSettingsSaving}
              className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded transition disabled:opacity-50"
            >
              {supportSettingsSaving ? 'Ukládám...' : '💾 Uložit nastavení'}
            </button>
          </div>

          <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-700 text-gray-400' : 'bg-blue-50 text-blue-700'} text-sm`}>
            <strong>Vzorec výpočtu provize:</strong> provize = (GP firmy z hlavní provize) × {((supportSettings.gpCoefficient || 0) * 100).toFixed(2)}% + pevná hodnota dle typu požadavku
          </div>
        </div>
      )}

      {activeTab === 'tools' && (
        <div>
          <h2 className={`text-xl font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Administrátorské nástroje</h2>
          
          <div className="space-y-6">
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} p-6 rounded-lg shadow`}>
              <h3 className={`text-lg font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Přepočet provizí</h3>
              <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'} mb-4`}>
                Přepočítá všechny provize pro vybraný měsíc podle aktuálních koeficientů a pravidel.
              </p>
              <div className="flex items-center gap-4">
                <select className={`border rounded px-3 py-2 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`} id="recalcMonth">
                  <option value="">Vyberte měsíc...</option>
                  {adminStats.monthlyStats?.map(month => (
                    <option key={month.month} value={month.month}>
                      {new Date(month.month + '-01').toLocaleDateString('cs-CZ', { year: 'numeric', month: 'long' })}
                    </option>
                  ))}
                </select>
                <button
                  onClick={() => {
                    const month = document.getElementById('recalcMonth').value;
                    if (month) recalculateCommissions(month);
                    else alert('Vyberte měsíc');
                  }}
                  disabled={loading}
                  className="bg-yellow-600 text-white px-4 py-2 rounded hover:bg-yellow-700 disabled:opacity-50"
                >
                  🔄 Přepočítat
                </button>
              </div>
            </div>

            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} p-6 rounded-lg shadow`}>
              <h3 className={`text-lg font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Přepočet zádržného</h3>
              <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'} mb-4`}>
                Přepočítá POUZE zádržné pro vybraný měsíc. Užitečné když potřebujete aktualizovat zádržné i pro měsíce bez dat.
              </p>
              <div className="flex items-center gap-4">
                <select className={`border rounded px-3 py-2 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`} id="recalcRetentionMonth">
                  <option value="">Vyberte měsíc...</option>
                  {adminStats.monthlyStats?.map(month => (
                    <option key={month.month} value={month.month}>
                      {new Date(month.month + '-01').toLocaleDateString('cs-CZ', { year: 'numeric', month: 'long' })}
                    </option>
                  ))}
                  {/* Přidej možnost zadat vlastní měsíc */}
                  <option value="custom">-- Vlastní měsíc --</option>
                </select>
                <button
                  onClick={() => {
                    const month = document.getElementById('recalcRetentionMonth').value;
                    if (month === 'custom') {
                      const customMonth = prompt('Zadejte měsíc ve formátu YYYY-MM (např. 2025-11):');
                      if (customMonth && /^\d{4}-\d{2}$/.test(customMonth)) {
                        recalculateRetention(customMonth);
                      } else if (customMonth) {
                        alert('Neplatný formát. Použijte YYYY-MM (např. 2025-11)');
                      }
                    } else if (month) {
                      recalculateRetention(month);
                    } else {
                      alert('Vyberte měsíc');
                    }
                  }}
                  disabled={loading}
                  className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50"
                >
                  💰 Přepočítat zádržné
                </button>
              </div>
            </div>

            <div className={`${isDarkMode ? 'bg-red-900 bg-opacity-20 border-2 border-red-600' : 'bg-red-50 border-2 border-red-300'} p-6 rounded-lg shadow`}>
              <h3 className={`text-lg font-semibold mb-3 ${isDarkMode ? 'text-red-400' : 'text-red-900'}`}>⚠️ Smazat zádržné</h3>
              <p className={`${isDarkMode ? 'text-red-300' : 'text-red-700'} mb-4`}>
                <strong>POZOR:</strong> Tato akce smaže všechny záznamy zádržného pro vybraný měsíc. Použijte pouze pokud potřebujete vymazat chybně vypočítaná data.
              </p>
              <div className="flex items-center gap-4">
                <select className={`border rounded px-3 py-2 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`} id="deleteRetentionMonth">
                  <option value="">Vyberte měsíc...</option>
                  {adminStats.monthlyStats?.map(month => (
                    <option key={month.month} value={month.month}>
                      {new Date(month.month + '-01').toLocaleDateString('cs-CZ', { year: 'numeric', month: 'long' })}
                    </option>
                  ))}
                  {/* Přidej možnost zadat vlastní měsíc */}
                  <option value="custom">-- Vlastní měsíc --</option>
                </select>
                <button
                  onClick={() => {
                    let month = document.getElementById('deleteRetentionMonth').value;
                    if (month === 'custom') {
                      month = prompt('Zadejte měsíc ve formátu YYYY-MM (např. 2025-11):');
                      if (!month || !/^\d{4}-\d{2}$/.test(month)) {
                        if (month) alert('Neplatný formát. Použijte YYYY-MM (např. 2025-11)');
                        return;
                      }
                    }
                    if (month) {
                      deleteRetention(month);
                    } else {
                      alert('Vyberte měsíc');
                    }
                  }}
                  disabled={loading}
                  className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 disabled:opacity-50"
                >
                  🗑️ Smazat zádržné
                </button>
              </div>
            </div>

            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} p-6 rounded-lg shadow`}>
              <h3 className={`text-lg font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Systémové informace</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <strong className={isDarkMode ? 'text-white' : 'text-gray-900'}>Databázové kolekce:</strong>
                  <ul className={`mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    <li>• Commissions (hlavní provize)</li>
                    <li>• StrategicCommissions (strategické)</li>
                    <li>• Cashbacks (srážky)</li>
                    <li>• UserCoefficients (koeficienty)</li>
                  </ul>
                </div>
                <div>
                  <strong className={isDarkMode ? 'text-white' : 'text-gray-900'}>Poslední aktivita:</strong>
                  <div className={`mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    {adminStats.lastActivity ? new Date(adminStats.lastActivity).toLocaleString('cs-CZ') : 'Neznámá'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'export' && (
        <div>
          <h2 className={`text-xl font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Export dat</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} p-6 rounded-lg shadow`}>
              <h3 className={`text-lg font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Export podle typu</h3>
              <div className="space-y-3">
                <button
                  onClick={() => exportData('commissions')}
                  className="w-full bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                >
                  📋 Hlavní provize (všechny)
                </button>
                <button
                  onClick={() => exportData('strategic-commissions')}
                  className="w-full bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700"
                >
                  🎯 Strategické provize (všechny)
                </button>
                <button
                  onClick={() => exportData('cashbacks')}
                  className="w-full bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
                >
                  ➖ Cashbacky (všechny)
                </button>
              </div>
            </div>

            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} p-6 rounded-lg shadow`}>
              <h3 className={`text-lg font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Export podle měsíce</h3>
              <div className="space-y-3">
                <select className={`w-full border rounded px-3 py-2 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`} id="exportMonth">
                  <option value="">Vyberte měsíc...</option>
                  {adminStats.monthlyStats?.map(month => (
                    <option key={month.month} value={month.month}>
                      {new Date(month.month + '-01').toLocaleDateString('cs-CZ', { year: 'numeric', month: 'long' })}
                    </option>
                  ))}
                </select>
                <button
                  onClick={() => {
                    const month = document.getElementById('exportMonth').value;
                    if (month) exportData('all', month);
                    else alert('Vyberte měsíc');
                  }}
                  className="w-full bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
                >
                  💾 Export celého měsíce
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal pro přidání koeficientu */}
      {showCoefficientModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg max-w-md w-full m-4 p-6`}>
            <h2 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              {editingCoefficient ? 'Upravit uživatelský koeficient' : 'Přidat uživatelský koeficient'}
            </h2>
            <form onSubmit={(e) => {
              console.log('🔍 EDIT DEBUG - Form submit, editingCoefficient:', editingCoefficient);
              if (editingCoefficient) {
                handleUpdateCoefficient(e);
              } else {
                handleAddCoefficient(e);
              }
            }}>
              <div className="space-y-4 mb-4">
                <div>
                  <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-1`}>Uživatel *</label>
                  {editingCoefficient ? (
                    <div className={`w-full border rounded px-3 py-2 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-300' : 'bg-gray-100 border-gray-300 text-gray-700'}`}>
                      {allUsers.find(u => u._id === newCoefficient.userId)?.name || 'Uživatel'}
                    </div>
                  ) : (
                    <select
                      required
                      value={newCoefficient.userId}
                      onChange={(e) => setNewCoefficient({...newCoefficient, userId: e.target.value})}
                      className={`w-full border rounded px-3 py-2 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                    >
                      <option value="">Vyberte uživatele...</option>
                      {allUsers.map(user => (
                        <option key={user._id} value={user._id}>{user.name}</option>
                      ))}
                    </select>
                  )}
                </div>
                <div>
                  <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-1`}>Koeficient *</label>
                  <input
                    type="number"
                    required
                    min="0"
                    step="0.01"
                    value={newCoefficient.coefficient}
                    onChange={(e) => setNewCoefficient({...newCoefficient, coefficient: e.target.value})}
                    className={`w-full border rounded px-3 py-2 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                    placeholder="1.0"
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-1`}>Popis</label>
                  <textarea
                    value={newCoefficient.description}
                    onChange={(e) => setNewCoefficient({...newCoefficient, description: e.target.value})}
                    className={`w-full border rounded px-3 py-2 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                    rows="2"
                    placeholder="Důvod nebo popis koeficientu..."
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={resetCoefficientModal}
                  className={`px-4 py-2 border rounded ${isDarkMode ? 'text-gray-300 border-gray-600 hover:bg-gray-700' : 'text-gray-600 border-gray-300 hover:bg-gray-50'}`}
                >
                  Zrušit
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                >
                  {loading ? (editingCoefficient ? 'Ukládám...' : 'Přidávám...') : (editingCoefficient ? 'Uložit' : 'Přidat')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal pro strategické koeficienty */}
      {showStrategicCoefficientModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 w-full max-w-md`}>
            <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              {editingStrategicCoefficient ? 'Upravit strategický koeficient' : 'Přidat strategický koeficient'}
            </h3>
            
            <form onSubmit={(e) => { 
              e.preventDefault(); 
              editingStrategicCoefficient ? handleUpdateStrategicCoefficient() : handleAddStrategicCoefficient(); 
            }}>
              <div className="space-y-4">
                <div>
                  <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-1`}>Uživatel *</label>
                  {editingStrategicCoefficient ? (
                    <div className={`w-full border rounded px-3 py-2 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-300' : 'bg-gray-100 border-gray-300 text-gray-700'}`}>
                      {allUsers.find(u => u._id === newStrategicCoefficient.userId)?.name || 'Uživatel'}
                    </div>
                  ) : (
                    <select
                      required
                      value={newStrategicCoefficient.userId}
                      onChange={(e) => setNewStrategicCoefficient({...newStrategicCoefficient, userId: e.target.value})}
                      className={`w-full border rounded px-3 py-2 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                    >
                      <option value="">Vyberte uživatele...</option>
                      {allUsers.map(user => (
                        <option key={user._id} value={user._id}>{user.name}</option>
                      ))}
                    </select>
                  )}
                </div>
                <div>
                  <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-1`}>Strategický koeficient *</label>
                  <input
                    type="number"
                    required
                    min="0"
                    step="0.01"
                    value={newStrategicCoefficient.coefficient}
                    onChange={(e) => setNewStrategicCoefficient({...newStrategicCoefficient, coefficient: e.target.value})}
                    className={`w-full border rounded px-3 py-2 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                    placeholder="1.0"
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-1`}>Popis</label>
                  <textarea
                    value={newStrategicCoefficient.description}
                    onChange={(e) => setNewStrategicCoefficient({...newStrategicCoefficient, description: e.target.value})}
                    className={`w-full border rounded px-3 py-2 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                    rows="2"
                    placeholder="Důvod nebo popis strategického koeficientu..."
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={resetStrategicCoefficientModal}
                  className={`px-4 py-2 rounded border ${isDarkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                >
                  Zrušit
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                >
                  {loading ? (editingStrategicCoefficient ? 'Ukládám...' : 'Přidávám...') : (editingStrategicCoefficient ? 'Uložit' : 'Přidat')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}